import React from "react";
import { Box, Dialog, Typography } from "@mui/material";
import { COLOR_CONSTANTS } from "../../../../constants/styles";
import { MyPage } from "./MyPage";

export type SideProfileButtonProps = {
  name: string;
  ariaLabel?: string;
  disabled?: boolean;
  mode?: "off" | "on" | "disabled";
};

function getNameAbbreviation(name: string): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "";

  const firstChar = trimmed.charAt(0);
  const code = firstChar.codePointAt(0) ?? 0;
  const isHangulSyllable = code >= 0xac00 && code <= 0xd7a3;

  if (isHangulSyllable) {
    return firstChar; // 한글: 앞 한 음절
  }

  // 영어: 앞 두 글자 (알파벳 기준)
  const latinMatch = trimmed.match(/[A-Za-z]{1,2}/);
  if (latinMatch) return latinMatch[0].toUpperCase();

  // 그 외: 첫 글자 반환
  return firstChar;
}

export function SideProfileButton({
  name,
  ariaLabel,
  disabled = false,
  mode = "off",
}: SideProfileButtonProps) {
  const [open, setOpen] = React.useState(false);

  const initials = React.useMemo(() => getNameAbbreviation(name), [name]);

  const handleOpen = React.useCallback(() => {
    if (disabled) return;
    setOpen(true);
  }, [disabled]);

  const handleClose = React.useCallback(() => {
    setOpen(false);
  }, []);

  const isDisabled = disabled || mode === "disabled";
  const isActive = mode === "on";

  const buttonBaseStyles = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    cursor: isDisabled ? "not-allowed" : "pointer",
    borderRadius: "8px",
    transition: "all 0.2s ease",
    position: "relative" as const,
    border: `1px solid ${COLOR_CONSTANTS._components.chip.defaultEnabledBorder}`,
    background: "transparent",
    padding: "4px",
  } as const;

  return (
    <>
      <Box
        component="button"
        aria-label={ariaLabel ?? "프로필"}
        onClick={isDisabled ? undefined : handleOpen}
        disabled={isDisabled}
        sx={{
          ...buttonBaseStyles,
          "&:hover": isDisabled
            ? undefined
            : {
                backgroundColor: COLOR_CONSTANTS.primary._states.hover,
              },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            height: "100%",
            borderRadius: "4px",
            // 내부 배경은 회색, 텍스트는 흰색
            backgroundColor: COLOR_CONSTANTS.COLOR.GREY[400],
            color: COLOR_CONSTANTS.common.white_states.main,
            // 오프/온 상태에 따른 외곽선/음영 표현
          }}
        >
          <Typography
            component="span"
            sx={{
              color: COLOR_CONSTANTS.common.white_states.main,
              fontWeight: 700,
              fontSize: 12,
              lineHeight: 1,
              userSelect: "none",
            }}
          >
            {initials}
          </Typography>
        </Box>
      </Box>

      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="side-profile-modal"
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            maxWidth: "840px",
            maxHeight: "654px",
            borderRadius: "8px",
          },
        }}
      >
        <MyPage onClose={handleClose} />
      </Dialog>
    </>
  );
}
